/**
 * Proyección: traduce el `kind` SEMÁNTICO de un FieldDescriptor (del diccionario
 * único @idc-forms/schemas `fields.ts`) a un formatter concreto del review, usando los
 * catálogos de la app web (países, enum labels, moneda). El diccionario describe QUÉ es
 * el dato (kind); este adaptador decide CÓMO se muestra — el engine/paquete queda libre
 * de catálogos de UI. Es la pieza que hace de `fields.ts` la única fuente del review.
 */

import type { FieldDescriptor, FieldKind, SectionFields } from '@idc-forms/schemas'
import { CivilStatusLabel } from '@idc-forms/types/civil-status'
import { GenderLabel } from '@idc-forms/types/gender'
import { IdentificationCardLabel } from '@idc-forms/types/identifications'
import { MigrationStatusLabel } from '@idc-forms/types/migration-status'
import { NegotiationPurposeLabel } from '@idc-forms/types/negotiation-purpose'
import { RelationshipLabel } from '@idc-forms/types/relationship'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { CATALOG_REASONS_ASSOCIATION } from '@/shared/catalogs/associations'
import countries from '@/shared/catalogs/countries.json'
import { CATALOG_TYPE_MODIFICATIONS } from '@/shared/catalogs/modifications'
import { CATALOG_ORIGIN_WEALTH_PROVENANCE } from '@/shared/catalogs/origin-wealths'
import { NAME_SOURCE_FUNDS } from '@/shared/catalogs/source-funds'
import { NAME_TERM_APPOINTMENTS } from '@/shared/catalogs/term-appointments'
import { departmentLabel, municipalityLabel } from './geo-labels'
import type { DetailFieldDef, SectionDetailConfig } from './section-detail-review'

const EMPTY = '—'

type Formatter = (value: unknown, record: Record<string, unknown>, path?: string) => string

/** Construye un lookup value→label desde un catálogo [{value,label}]. */
function lookup(catalog: Array<{ value: string; label: string }>): Formatter {
  const map = new Map(catalog.map((o) => [o.value, o.label]))
  return (v) => (v == null || v === '' ? EMPTY : (map.get(String(v)) ?? String(v)))
}

/** Lookup desde un Record<string,string> (enum labels). */
function fromLabels(labels: Record<string, string>): Formatter {
  return (v) => (v == null || v === '' ? EMPTY : (labels[String(v)] ?? String(v)))
}

const COUNTRY_BY_ISO = new Map(
  (countries as { isoCode: string; name: { es: string } }[]).map((c) => [c.isoCode, c.name.es])
)
const country: Formatter = (v) =>
  v == null || v === '' ? EMPTY : (COUNTRY_BY_ISO.get(String(v)) ?? String(v))

const nationality: Formatter = (v) => {
  const list = Array.isArray(v) ? v : []
  return list.length > 0 ? list.map((x) => country(x, {})).join(', ') : EMPTY
}

const money: Formatter = (v) => {
  if (v == null || v === '') return EMPTY
  const n = typeof v === 'string' ? Number(v) : (v as number)
  if (Number.isNaN(n)) return EMPTY
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    currencyDisplay: 'narrowSymbol'
  }).format(n)
}

const date: Formatter = (v) => {
  if (typeof v !== 'string' || !v) return EMPTY
  try {
    return format(parseISO(v), "d 'de' MMM yyyy", { locale: es })
  } catch {
    return v
  }
}

const yesNo: Formatter = (v) => (v ? 'Sí' : 'No')

/** Nombre completo a partir de subdocumentos name/lastName del registro. */
const fullName: Formatter = (_v, record) => {
  const name = (record.name ?? {}) as Record<string, unknown>
  const last = (record.lastName ?? {}) as Record<string, unknown>
  const parts = [name.first, name.second, last.first, last.second].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : EMPTY
}

const purpose: Formatter = (v, record) => {
  if (v === 'OTHER') return String(record.otherPurpose ?? 'Otro propósito')
  return v == null || v === ''
    ? EMPTY
    : (NegotiationPurposeLabel[v as keyof typeof NegotiationPurposeLabel] ?? String(v))
}

const originWealth: Formatter = (v) => {
  const list = Array.isArray(v) ? v : []
  if (list.length === 0) return EMPTY
  const fmt = lookup(CATALOG_ORIGIN_WEALTH_PROVENANCE)
  return list.map((x) => fmt(x, {})).join(', ')
}

/** Fuente de fondos jurídica: etiqueta del catálogo; si es "Otro" ('9'), su detalle. */
const sourceFund: Formatter = (v, record) => {
  if (v == null || v === '') return EMPTY
  if (String(v) === '9') return String(record.otherType ?? 'Otro')
  return NAME_SOURCE_FUNDS[Number(v) as keyof typeof NAME_SOURCE_FUNDS] ?? String(v)
}

const department: Formatter = (v) => departmentLabel(v)

/** Municipio: resuelve el nombre con el `department` hermano (mismo parent que el path). */
const municipality: Formatter = (v, record, path) => {
  const deptPath = path?.replace(/municipality$/, 'department')
  const dept = deptPath
    ? deptPath
        .split('.')
        .reduce<unknown>(
          (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
          record
        )
    : undefined
  return municipalityLabel(v, dept)
}

const gender = fromLabels(GenderLabel)
const civilStatus = fromLabels(CivilStatusLabel)
const migration = fromLabels(MigrationStatusLabel)
const relationship = fromLabels(RelationshipLabel)
const association = lookup(CATALOG_REASONS_ASSOCIATION)
const modification = lookup(CATALOG_TYPE_MODIFICATIONS)

/**
 * Formatter por enumKey (cuando kind === 'enum').
 *
 * ⚠️ REGISTRO GEMELO. El otro es `ENUM_LABELS` en `applicant-field-catalog.ts`. Este sirve a las
 * secciones config-driven (económica, patrimonial, empresa, PEP, representante legal); aquél a la
 * pantalla del SOLICITANTE, que tiene catálogo propio. **Un `enumKey` nuevo va en LOS DOS.**
 * Registrarlo en uno solo no rompe nada visible: la superficie que quedó afuera pinta el valor
 * CRUDO del dominio —el asesor lee «OWNED» en un expediente KYC— y ningún typecheck lo nota.
 * Pasó con `residence.housingType`, un campo que ya no existe — la lección sí. Checklist
 * completo: `docs/architecture/client-field-checklist.md`.
 */
const ENUM_FORMATTERS: Record<string, Formatter> = {
  identification: fromLabels(IdentificationCardLabel),
  gender,
  civilStatus,
  migration,
  relationship,
  termAppointment: fromLabels(NAME_TERM_APPOINTMENTS)
}

/**
 * Formatter del review para un descriptor, según su `kind`. `text`/`number` no llevan
 * formatter (el review usa su default). Devuelve `undefined` para esos (default).
 */
export function formatterForKind(kind: FieldKind, enumKey?: string): Formatter | undefined {
  switch (kind) {
    case 'boolean':
      return yesNo
    case 'date':
      return date
    case 'money':
      return money
    case 'country':
      return country
    case 'department':
      return department
    case 'municipality':
      return municipality
    case 'nationality':
      return nationality
    case 'fullName':
      return fullName
    case 'purpose':
      return purpose
    case 'association':
      return association
    case 'originWealth':
      return originWealth
    case 'sourceFund':
      return sourceFund
    case 'modification':
      return modification
    case 'enum':
      return enumKey ? ENUM_FORMATTERS[enumKey] : undefined
    case 'profession':
      // En el detalle de review no hay contexto de profesiones (no recibe props);
      // se muestra el valor crudo, igual que el config hecho a mano del representante.
      return undefined
    case 'text':
    case 'number':
    default:
      return undefined
  }
}

/** Descriptor del diccionario → campo del detalle de review (proyección). */
export function toDetailField(descriptor: FieldDescriptor): DetailFieldDef {
  const format = formatterForKind(descriptor.kind, descriptor.enumKey)
  return format
    ? { path: descriptor.path, label: descriptor.label, format }
    : {
        path: descriptor.path,
        label: descriptor.label
      }
}

/**
 * Proyección completa: los campos de una sección del diccionario (`fields.ts`) → la
 * config del detalle de review. Reemplaza las configs escritas a mano en
 * `section-detail-configs.ts`, dejando `fields.ts` como única fuente.
 */
export function toSectionDetailConfig(fields: SectionFields): SectionDetailConfig {
  return {
    scalarGroups: fields.scalarGroups?.map((group) => ({
      title: group.title,
      fields: group.fields.map(toDetailField)
    })),
    arrays: fields.arrays?.map((array) => ({
      path: array.path,
      itemTitle: array.itemTitle,
      fields: array.fields.map(toDetailField)
    }))
  }
}
